<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Hooks
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { getExtSensorsSettings } from '../../../helpers/ext_sensor_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { valueOutOfRange } from '../../../helpers/utilities';
	import { getNumberInputConfig } from '../../../helpers/number_input_helpers';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Input, Button, Text } from '@Shelly/shelly-design-system';
	//Types
	import type { Request } from '../../../types/requests/request';
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';

	export let device: Device;

	let waitForResponse = false;

	const id = real_id(device.id),
		settingsSnapshot = readDeviceSettings(id),
		ext_temperature_settings = getExtSensorsSettings(device, 'temperature'),
		tunit = 't' + (settingsSnapshot?.ext_sensors?.temperature_unit || 'C'),
		tname = `offset_${tunit}`,
		min = { tC: -50, tF: -90 }[tunit],
		max = { tC: 50, tF: 90 }[tunit],
		setOffset = async (sensor: any, currentValue): Promise<Request> => {
			const offset = sensor[tname];
			if (offset != currentValue) {
				const local_ip = get_local_ip(id);
				if (local_ip) {
					return await localReq(
						device,
						`${local_ip}/settings/ext_temperature/${sensor.extid}`,
						'post',
						{
							[tname]: offset,
						},
					);
				}
				return await postReq(`/device/${device.category}/settings/temperature_offset`, {
					channel: device.channel,
					extid: sensor.extid,
					offset,
					tunit,
					id,
				});
			}
			return { isok: true };
		},
		save = async () => {
			waitForResponse = true;
			const settings = devices_settings.getByRealId(id)?.ext_temperature,
				res: SettingRequest[] = await Promise.all(
					ext_temperature_settings.map((i) => {
						const channelSetting =
							device.channels_count > 1
								? settings?.[i.extid][device.channel]
								: settings?.[i.extid];
						return setOffset(i, channelSetting?.[tname]);
					}),
				);
			onSettingsResponse(res, device);
			waitForResponse = false;
		};

	$: errors = ext_temperature_settings.some((i) => {
		valueOutOfRange(i[tname], min, max);
	});
</script>

{#each ext_temperature_settings as sensor}
	<Text styleType="body-small" class="mb-s">{sensor.name}</Text>
	<Input
		aria-label={$format('input_temperature_offset', {
			default: 'Input Temperature offset',
		})}
		{...getNumberInputConfig()}
		size="m"
		class="mb-s"
		icon="fas fa-calculator"
		value={sensor[tname]}
		error={valueOutOfRange(sensor[tname], min, max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => (sensor[tname] = e.target['value'])}
		id="input-x7wmvf0"
	/>
{/each}
<Button size="m" width="full" loading={waitForResponse} disabled={errors} on:click={save}
	>{$format('button_save', { default: 'Save' })}</Button
>
